import { defineStore } from "pinia";
import { CategoriesApi } from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { ICategoriesStore } from "~/utils/interfaces/categories-interfaces";

export const useCategoriesStore = defineStore("categoriesStore", {
  state: (): ICategoriesStore => {
    return {
      categories: null,
      editDetails: false,
    };
  },
  getters: {},
  actions: {
    async fetchCategories() {
      const feedback = useFeedback();
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        const data = await api.findCompanyCategories();
        this.categories = data;
      } catch {
        feedback.error("Erreur lors de la récupération des catégories", "small");
      }
    },
    async createCategory(name: string) {
      const feedback = useFeedback();
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        await api.createCategory({ createCategoryDto: { name } });
        await this.fetchCategories();
        feedback.success("Catégorie créée", "small");
      } catch {
        feedback.error("Erreur lors de la création", "small");
      }
    },
    async updateCategoryName(categoryId: string, newName: string) {
      const feedback = useFeedback();
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        await api.updateCategory({ id: categoryId, updateCategoryDto: { name: newName } });
        await this.fetchCategories();
        feedback.success("Catégorie modifiée", "small");
      } catch {
        feedback.error("Erreur lors de la mise à jour", "small");
      }
    },
    async deleteCategory(categoryId: string) {
      const feedback = useFeedback();
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        await api.deleteCategory({ id: categoryId });
        await this.fetchCategories();
        feedback.success("Catégorie supprimée", "small");
      } catch {
        feedback.error("Erreur lors de la suppression", "small");
      }
    },
    /**
     * Drops the categories a training form invented but never created — `selectNewCategory` pushes
     * one with no id as soon as a name is typed, so abandoning the form has to take it back. Only
     * those: the list is shared with the settings section and the catalog dropdowns, both of which
     * a blunt reset would empty on screen.
     */
    dropUnsavedCategories() {
      if (!this.categories) return;

      this.categories = this.categories.filter((category) => !!category.categoryId);
    },
    async mergeCategory(sourceCategoryId: string, targetCategoryId: string) {
      const feedback = useFeedback();
      try {
        const api = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        await api.mergeCategories({
          mergeCategoriesDto: { sourceCategoryId, targetCategoryId },
        });
        await this.fetchCategories();
      } catch {
        feedback.error("Erreur lors de la fusion", "small");
      }
    },
  },
});
